import { Component, inject, OnInit } from '@angular/core';

import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TRANSLOCO_SCOPE, TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { AuthService } from '../../../../services/auth.service';
import { UserService } from '../../../../services/user.service';
import { ApiUtils } from '../../../../core/helper/api.utils';

@Component({
  selector: 'app-verify-email',
  imports: [TranslocoDirective, RouterModule],
  templateUrl: './verify-email.component.html',
  styleUrl: './verify-email.component.css',
  standalone: true,
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'auth' }]
})
export class VerifyEmailComponent implements OnInit {
  private readonly _authService = inject(AuthService);
  private readonly _userService = inject(UserService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _translocoService = inject(TranslocoService);

  public verificationToken = '';
  public isLoading = true;
  public isSuccess = false;
  /** True when the backend returns EmailAlreadyVerified (show success + link to login). */
  public isAlreadyVerified = false;
  public errorMessage = '';

  ngOnInit(): void {
    this.verificationToken = this._route.snapshot.paramMap.get('token') || '';
    if (!this.verificationToken) {
      this.errorMessage = 'No verification token provided';
      this.isLoading = false;
      return;
    }

    this.verifyEmail();
  }

  public async verifyEmail(): Promise<void> {
    try {
      await this._authService.verifyEmail(this.verificationToken);
      this.isSuccess = true;

      // Load user data after successful verification
      await this._userService.loadCurrentUser();

      // Redirect to dashboard after successful verification
      this._router.navigate(['/main/dashboard']);
    } catch (error) {
      const appError = ApiUtils.createAppErrorFromResponse(error);
      if (appError.errorCode === 'EmailAlreadyVerified') {
        this.isAlreadyVerified = true;
      } else {
        this.errorMessage =
          appError.errorCode === 'TokenNotFound'
            ? this._translocoService.translate('verifyEmail.tokenNotFound', {}, 'auth')
            : this._translocoService.translate('verifyEmail.errorMessage', {}, 'auth');
      }
    } finally {
      this.isLoading = false;
    }
  }
}
